// src/components/shared/SignOutDialog.jsx
//
// Asks before signing out, and says exactly what will happen to this
// person's writing on this device, which depends on the kind of account
// (services/HubSessionService.js sessionOwnership). Rendered into
// document.body: the nav that opens it has a backdrop blur, which would trap
// a `fixed` overlay inside the nav bar.
//
// Australian English throughout.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LogOut } from 'lucide-react';
import { signOutEverywhere, signOutKind } from '../../services/SignOutService';

const COPY = {
  hub: [
    'Your toolkit writing and Vibe Check results are backed up under your random ID.',
    'Signing out removes your toolkit writing from this device and disconnects it from that backup. To get it back, here or on another device, you will need a recovery code. If you have not got one, cancel and get one from the box at the bottom of the Tools page first.',
  ],
  native: [
    'Your toolkit writing is saved only on this device, so signing out deletes it from here for good.',
    'If you want a copy, cancel and use Download My Data on the Privacy & Data page first.',
  ],
  none: ['This signs you out on this device.'],
};

export default function SignOutDialog({ open, onCancel, onSignedOut }) {
  const [kind, setKind] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setKind(null);
    signOutKind().then((k) => { if (live) setKind(k); });
    return () => { live = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open || typeof document === 'undefined') return null;

  const confirm = async () => {
    setBusy(true);
    const result = await signOutEverywhere();
    onSignedOut?.(result);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      role="presentation"
      onClick={() => { if (!busy) onCancel?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-out-title"
        className="w-full max-w-md rounded-2xl border border-slate-600 bg-slate-800 p-6 text-left shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sign-out-title" className="mb-3 flex items-center gap-2 text-xl font-bold text-white">
          <LogOut className="h-5 w-5 text-purple-300" aria-hidden="true" />
          Sign out?
        </h2>
        {kind === null ? (
          <p className="text-sm text-slate-300">Checking what is saved on this device...</p>
        ) : (
          <div className="space-y-2 text-sm leading-relaxed text-slate-300">
            {COPY[kind].map((line) => <p key={line}>{line}</p>)}
          </div>
        )}
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg bg-slate-700 px-4 py-2 text-white hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy || kind === null}
            className="rounded-lg bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-500 disabled:opacity-50"
          >
            {busy ? 'Signing out...' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
